/* Copyright (c) 2026 Dobler Data Solutions, LLC. All rights reserved. */

import { useState, useCallback, useRef } from 'react';
import type { TaxonomyCodeDto } from '@shared/types';
import { useFormulaListStore } from '../../stores/formulaListStore';
import { BatchTaxonomyPicker } from '../assignment/BatchTaxonomyPicker';
import { batchAssign } from '../../services/formularyApi';
import { getAccessToken } from '../../services/apiClient';
import { Tooltip } from '../layout/Tooltip';
import { trackEvent } from '../../services/telemetry';

const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api';

interface BatchProgress {
  current: number;
  total: number;
  success: number;
  failed: number;
}

export function BatchAssignBar() {
  const { selectedSetIds, clearSelection, loadFormulas } = useFormulaListStore();
  const [showPicker, setShowPicker] = useState(false);
  const [selectedTaxonomies, setSelectedTaxonomies] = useState<TaxonomyCodeDto[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const [progress, setProgress] = useState<BatchProgress | null>(null);
  const [result, setResult] = useState<{ success: number; failed: number; duplicateCount?: number; failures?: Array<{ formulaSetId: string; error: string }> } | null>(null);
  const eventSourceRef = useRef<EventSource | null>(null);

  const handlePickerDone = useCallback((taxonomies: TaxonomyCodeDto[]) => {
    setSelectedTaxonomies(taxonomies);
    setShowPicker(false);
  }, []);

  const handleRemoveTaxonomy = useCallback((setId: string) => {
    setSelectedTaxonomies((prev) => prev.filter((t) => t.setId !== setId));
  }, []);

  const handleAssign = useCallback(async () => {
    const formulaSetIds = [...selectedSetIds];
    if (formulaSetIds.length === 0 || selectedTaxonomies.length === 0) return;

    setIsRunning(true);
    setProgress(null);
    setResult(null);

    // Open SSE connection BEFORE the batch request so we don't miss progress events.
    // getAccessToken() throws when dev auth bypass is active — skip SSE in that case.
    let es: EventSource | null = null;
    try {
      const token = await getAccessToken();
      es = new EventSource(`${API_BASE}/events?token=${encodeURIComponent(token)}`, { withCredentials: true });
      eventSourceRef.current = es;
      es.addEventListener('batch-progress', (e) => {
        try {
          const data = JSON.parse(e.data) as BatchProgress;
          setProgress(data);
        } catch { /* ignore parse errors */ }
      });
      await new Promise((resolve) => {
        es!.onopen = resolve;
        setTimeout(resolve, 500);
      });
    } catch { /* no token (dev bypass) — proceed without SSE progress */ }

    try {
      const response = await batchAssign({
        formulaSetIds,
        taxonomyCodeSetIds: selectedTaxonomies.map((t) => t.setId),
      });
      setResult({
        success: response.data.successCount,
        failed: response.data.failureCount,
        duplicateCount: response.data.duplicateCount,
        failures: response.data.failures,
      });
      trackEvent('taxonomy_batch_assigned', { count: formulaSetIds.length });
      clearSelection();
      setSelectedTaxonomies([]);
      loadFormulas(true);
    } catch {
      setResult({ success: 0, failed: formulaSetIds.length });
    } finally {
      es?.close();
      eventSourceRef.current = null;
      setIsRunning(false);
      setProgress(null);
    }
  }, [selectedSetIds, selectedTaxonomies, clearSelection, loadFormulas]);

  if (selectedSetIds.size === 0) return null;

  const totalOps = selectedSetIds.size * selectedTaxonomies.length;

  return (
    <>
      <div data-testid="batch-assign-bar" className="bg-dds-blue-50 border border-dds-blue-200 rounded-lg p-3 mb-4">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-sm font-medium text-dds-blue-700">
            {selectedSetIds.size} formula{selectedSetIds.size > 1 ? 's' : ''} selected
          </span>

          <Tooltip text="Open the taxonomy picker to choose codes for all selected formulas">
            <button
              data-testid="pick-taxonomy-btn"
              onClick={() => setShowPicker(true)}
              disabled={isRunning}
              className="bg-dds-blue-500 text-white text-sm px-3 py-1.5 rounded hover:bg-dds-blue-600 disabled:opacity-50"
            >
              {selectedTaxonomies.length > 0 ? 'Edit Taxonomy Selection' : 'Batch Assign Taxonomy'}
            </button>
          </Tooltip>

          {result && !result.failures?.length && (
            <span className="text-sm flex items-center gap-2">
              <span className="text-green-600">{result.success} assigned</span>
              {!!result.duplicateCount && (
                <span className="text-yellow-600">{result.duplicateCount} skipped (already assigned)</span>
              )}
            </span>
          )}

          <button
            data-testid="batch-clear-btn"
            onClick={() => { clearSelection(); setSelectedTaxonomies([]); setResult(null); }}
            className="text-sm text-gray-500 hover:text-gray-700 ml-auto"
          >
            Clear
          </button>
        </div>

        {/* Selected taxonomy chips + confirm */}
        {selectedTaxonomies.length > 0 && (
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <span className="text-xs text-gray-500">Assign:</span>
            {selectedTaxonomies.map((t) => (
              <span
                key={t.setId}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-dds-blue-100 text-dds-blue-700 text-xs font-medium"
              >
                {t.code} — {t.displayName}
                <button
                  onClick={() => handleRemoveTaxonomy(t.setId)}
                  className="text-dds-blue-400 hover:text-dds-blue-700 ml-0.5"
                >
                  ×
                </button>
              </span>
            ))}
            <Tooltip text={`Assign ${selectedTaxonomies.length} taxonomy code(s) to ${selectedSetIds.size} formula(s) — creates version chain entries`}>
              <button
                data-testid="batch-confirm-btn"
                onClick={handleAssign}
                disabled={isRunning}
                className="bg-green-600 text-white text-xs px-3 py-1 rounded hover:bg-green-700 disabled:opacity-50 ml-2"
              >
                Assign ({selectedTaxonomies.length} code{selectedTaxonomies.length > 1 ? 's' : ''} → {selectedSetIds.size} formula{selectedSetIds.size > 1 ? 's' : ''})
              </button>
            </Tooltip>
          </div>
        )}
      </div>

      {/* Batch result error panel */}
      {result && result.failed > 0 && result.failures && result.failures.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-semibold text-red-700">
              {result.failed} formula{result.failed > 1 ? 's' : ''} failed
              {result.success > 0 && <span className="text-green-600 font-normal ml-2">({result.success} succeeded)</span>}
            </h4>
            <button
              onClick={() => setResult(null)}
              className="text-xs text-red-400 hover:text-red-600"
            >
              Dismiss
            </button>
          </div>
          <ul className="space-y-1 max-h-40 overflow-y-auto">
            {result.failures.map((f, i) => (
              <li key={i} className="text-xs text-red-600 flex gap-2">
                <span className="text-red-400 font-mono shrink-0">{f.formulaSetId.slice(0, 8)}…</span>
                <span>{f.error}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Taxonomy picker modal */}
      {showPicker && (
        <BatchTaxonomyPicker
          onDone={handlePickerDone}
          onCancel={() => setShowPicker(false)}
          formulaCount={selectedSetIds.size}
          initialSelected={selectedTaxonomies}
        />
      )}

      {/* Progress overlay */}
      {isRunning && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-sm">
            <h3 className="text-base font-bold text-gray-900 mb-3">Batch Assigning...</h3>
            <div className="mb-2">
              <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-dds-blue-500 rounded-full transition-all duration-300"
                  style={{ width: `${progress ? Math.round((progress.current / progress.total) * 100) : 0}%` }}
                />
              </div>
            </div>
            <div className="text-sm text-gray-600 text-center">
              {progress
                ? `${progress.current} / ${progress.total} operations (${progress.success} succeeded${progress.failed > 0 ? `, ${progress.failed} failed` : ''})`
                : `Preparing ${totalOps} operations...`
              }
            </div>
          </div>
        </div>
      )}
    </>
  );
}
